// AmountCorrection — full Booking & Order editor. Load a record by id, then
// edit EVERY key field (guest details, dates, counts, discount, amount, rooms,
// payment, order items, table no...) and save. The backend keeps the ledger /
// pending in sync with the new amount.
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Grid,
  Typography,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  Chip,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import config from '../../config';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const Cur = company?.currency ?? '₹';
const token = sessionStorage.getItem('token');
const userId = Number(sessionStorage.getItem('userId')) || 0;

const authHeaders = {
  'Content-Type': 'application/json',
  Authorization: `Bearer ${token}`,
};

// ISO datetime → value for <input type="datetime-local"> (yyyy-MM-ddTHH:mm).
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

function useSnack() {
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const show = (msg, sev = 'success') => setSnack({ open: true, msg, sev });
  const node = (
    <Snackbar
      open={snack.open}
      autoHideDuration={4000}
      onClose={() => setSnack((s) => ({ ...s, open: false }))}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
      <Alert severity={snack.sev} variant='filled'>
        {snack.msg}
      </Alert>
    </Snackbar>
  );
  return [show, node];
}

// ───────────────────────── Booking editor ──────────────────────────────────
function BookingEditor() {
  const [idInput, setIdInput] = useState('');
  const [bk, setBk] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [newRoomNo, setNewRoomNo] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [roomBusy, setRoomBusy] = useState(false);
  const [showSnack, snackNode] = useSnack();

  const set = (k, v) => setBk((b) => ({ ...b, [k]: v }));

  const loadRooms = async (id) => {
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/correction/booking-rooms/${id}`,
        { headers: authHeaders },
      );
      setRooms(res.ok ? await res.json() : []);
    } catch {
      setRooms([]);
    }
  };

  const load = async () => {
    const id = parseInt(idInput, 10);
    if (!id || id <= 0) return showSnack('Enter a valid booking id.', 'warning');
    setLoading(true);
    setBk(null);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/correction/booking-full/${id}`,
        { headers: authHeaders },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return showSnack(data?.error || 'Not found.', 'error');
      setBk({
        ...data,
        checkIn: toLocalInput(data.checkIn),
        checkOut: toLocalInput(data.checkOut),
      });
      loadRooms(data.id);
    } catch (e) {
      showSnack(e.message || 'Failed to load.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!bk) return;
    setSaving(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/correction/booking-full`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          Id: bk.id,
          FirstName: bk.firstName || '',
          LastName: bk.lastName || '',
          MobileNumber: bk.mobileNumber || '',
          Email: bk.email || '',
          Address: bk.address || '',
          City: bk.city || '',
          CheckIn: bk.checkIn || null,
          CheckOut: bk.checkOut || null,
          AdultCount: Number(bk.adultCount) || 0,
          ChildCount: Number(bk.childCount) || 0,
          DiscountType: bk.discountType || '',
          DiscountValue: Number(bk.discountValue) || 0,
          FinalPrice: Number(bk.finalPrice) || 0,
          PaymentMode: bk.paymentMode || '',
          BookingRemark: bk.bookingRemark || '',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return showSnack(data?.error || 'Save failed.', 'error');
      showSnack('Booking updated.');
    } catch (e) {
      showSnack(e.message || 'Save failed.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteRoom = async (roomNumber) => {
    setRoomBusy(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/correction/delete-room`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ BookingId: bk.id, RoomNumber: roomNumber }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return showSnack(data?.error || 'Delete failed.', 'error');
      showSnack('Room removed.');
      loadRooms(bk.id);
    } catch (e) {
      showSnack(e.message || 'Delete failed.', 'error');
    } finally {
      setRoomBusy(false);
    }
  };

  const changeRoom = async (roomNumber) => {
    const target = parseInt(newRoomNo[roomNumber], 10);
    if (!target || target <= 0) return showSnack('Enter new room number.', 'warning');
    setRoomBusy(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/correction/change-room`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          BookingId: bk.id,
          RoomNumber: roomNumber,
          NewRoomNumber: target,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return showSnack(data?.error || 'Change failed.', 'error');
      showSnack('Room changed.');
      setNewRoomNo((m) => ({ ...m, [roomNumber]: '' }));
      loadRooms(bk.id);
    } catch (e) {
      showSnack(e.message || 'Change failed.', 'error');
    } finally {
      setRoomBusy(false);
    }
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #1565c022' }}>
      <Typography variant='h6' fontWeight={800} sx={{ color: '#1565c0', mb: 1.5 }}>
        Edit Booking
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <TextField
          fullWidth
          size='small'
          type='number'
          label='Booking ID'
          value={idInput}
          onChange={(e) => setIdInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
        />
        <Button
          variant='outlined'
          onClick={load}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <SearchIcon />}
          sx={{ whiteSpace: 'nowrap' }}>
          Load
        </Button>
      </Box>

      {bk && (
        <>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <TextField fullWidth size='small' label='First Name' value={bk.firstName || ''} onChange={(e) => set('firstName', e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size='small' label='Last Name' value={bk.lastName || ''} onChange={(e) => set('lastName', e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size='small' label='Mobile' value={bk.mobileNumber || ''} onChange={(e) => set('mobileNumber', e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size='small' label='Email' value={bk.email || ''} onChange={(e) => set('email', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size='small' label='Address' value={bk.address || ''} onChange={(e) => set('address', e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size='small' label='City' value={bk.city || ''} onChange={(e) => set('city', e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth size='small' label='Payment Mode' value={bk.paymentMode || ''} onChange={(e) => set('paymentMode', e.target.value)}>
                {['Cash', 'Card', 'Online', 'UPI', 'Due', 'Credit'].map((m) => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size='small' type='datetime-local' label='Check In' InputLabelProps={{ shrink: true }} value={bk.checkIn || ''} onChange={(e) => set('checkIn', e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size='small' type='datetime-local' label='Check Out' InputLabelProps={{ shrink: true }} value={bk.checkOut || ''} onChange={(e) => set('checkOut', e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size='small' type='number' label='Adults' value={bk.adultCount ?? 0} onChange={(e) => set('adultCount', e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size='small' type='number' label='Children' value={bk.childCount ?? 0} onChange={(e) => set('childCount', e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth size='small' label='Discount Type' value={bk.discountType || ''} onChange={(e) => set('discountType', e.target.value)}>
                <MenuItem value=''>None</MenuItem>
                <MenuItem value='Flat'>Flat</MenuItem>
                <MenuItem value='Percentage'>Percentage</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size='small' type='number' label='Discount Value' value={bk.discountValue ?? 0} onChange={(e) => set('discountValue', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size='small' type='number' label='Final Amount (with GST)' value={bk.finalPrice ?? 0} onChange={(e) => set('finalPrice', e.target.value)} InputProps={{ startAdornment: <InputAdornment position='start'>{Cur}</InputAdornment> }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size='small' label='Remark' value={bk.bookingRemark || ''} onChange={(e) => set('bookingRemark', e.target.value)} />
            </Grid>
          </Grid>

          <Button fullWidth variant='contained' onClick={save} disabled={saving} startIcon={saving ? <CircularProgress size={16} color='inherit' /> : <SaveIcon />} sx={{ mt: 2, bgcolor: '#1565c0' }}>
            {saving ? 'Saving...' : 'Save Booking'}
          </Button>

          {/* Rooms */}
          {rooms.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant='subtitle2' fontWeight={800} sx={{ mb: 1 }}>
                Rooms ({rooms.length})
              </Typography>
              {rooms.map((rm) => (
                <Box key={rm.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  <Chip label={`Room ${rm.roomNumber}`} size='small' color='primary' sx={{ minWidth: 78 }} />
                  <TextField size='small' type='number' placeholder='New no.' value={newRoomNo[rm.roomNumber] || ''} onChange={(e) => setNewRoomNo((m) => ({ ...m, [rm.roomNumber]: e.target.value }))} sx={{ width: 100 }} />
                  <Button size='small' variant='outlined' disabled={roomBusy} onClick={() => changeRoom(rm.roomNumber)}>Change</Button>
                  <Button size='small' variant='outlined' color='error' disabled={roomBusy || rooms.length <= 1} onClick={() => deleteRoom(rm.roomNumber)}>Delete</Button>
                </Box>
              ))}
            </>
          )}

          <LedgerEditor
            kind='booking'
            ownerId={bk.id}
            title='Booking Ledger'
            color='#1565c0'
          />
          <InvoiceEditor bookingId={bk.id} />
        </>
      )}
      {snackNode}
    </Paper>
  );
}

// ───────────────────────── Order editor ────────────────────────────────────
function OrderEditor() {
  const [idInput, setIdInput] = useState('');
  const [od, setOd] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSnack, snackNode] = useSnack();

  const setItem = (id, k, v) =>
    setItems((arr) => arr.map((it) => (it.id === id ? { ...it, [k]: v } : it)));

  const lineSum = items
    .filter((it) => !it.remove)
    .reduce((s, it) => s + (Number(it.price) || 0) * (Number(it.qty) || 0), 0);

  const load = async () => {
    const id = parseInt(idInput, 10);
    if (!id || id <= 0) return showSnack('Enter a valid order id.', 'warning');
    setLoading(true);
    setOd(null);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/correction/order-full/${id}`,
        { headers: authHeaders },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return showSnack(data?.error || 'Not found.', 'error');
      setOd(data);
      setItems((data.items || []).map((it) => ({ ...it, remove: false })));
    } catch (e) {
      showSnack(e.message || 'Failed to load.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!od) return;
    setSaving(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/correction/order-full`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          Id: od.id,
          Tableno: od.tableno || '',
          FinalPrice: 0, // 0 → backend recomputes from the line items
          Items: items.map((it) => ({
            Id: it.id,
            Qty: Number(it.qty) || 0,
            Price: Number(it.price) || 0,
            Remove: !!it.remove,
          })),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return showSnack(data?.error || 'Save failed.', 'error');
      showSnack('Order updated.');
      load();
    } catch (e) {
      showSnack(e.message || 'Save failed.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #2e7d3222' }}>
      <Typography variant='h6' fontWeight={800} sx={{ color: '#2e7d32', mb: 1.5 }}>
        Edit Order
      </Typography>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          size='small'
          type='number'
          label='Order ID'
          value={idInput}
          onChange={(e) => setIdInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
        />
        <Button
          variant='outlined'
          onClick={load}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <SearchIcon />}
          sx={{ whiteSpace: 'nowrap' }}>
          Load
        </Button>
      </Box>

      {od && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant='body2' color='text.secondary'>
              Order #{od.id} ·{' '}
              {od.bookingId > 0 ? `Room (Booking #${od.bookingId})` : 'Table'}
            </Typography>
            <Typography variant='body2' fontWeight={700}>
              {od.isPaid ? 'Paid' : od.isInvoice ? 'Invoiced' : 'Open'}
            </Typography>
          </Box>

          {od.bookingId > 0 ? null : (
            <TextField
              fullWidth
              size='small'
              label='Table No'
              value={od.tableno || ''}
              onChange={(e) => setOd((o) => ({ ...o, tableno: e.target.value }))}
              sx={{ mb: 2 }}
            />
          )}

          <TableContainer sx={{ border: '1px solid #eee', borderRadius: 2 }}>
            <Table size='small'>
              <TableHead>
                <TableRow sx={{ background: '#f1f8e9' }}>
                  {['Item', 'Qty', `Price (${Cur})`, 'Total', ''].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align='center'>
                      No items.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((it) => (
                    <TableRow
                      key={it.id}
                      sx={{ opacity: it.remove ? 0.4 : 1 }}>
                      <TableCell sx={{ fontWeight: 600 }}>{it.name}</TableCell>
                      <TableCell>
                        <TextField
                          size='small'
                          type='number'
                          value={it.qty}
                          disabled={it.remove}
                          onChange={(e) => setItem(it.id, 'qty', e.target.value)}
                          sx={{ width: 70 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size='small'
                          type='number'
                          value={it.price}
                          disabled={it.remove}
                          onChange={(e) => setItem(it.id, 'price', e.target.value)}
                          sx={{ width: 90 }}
                        />
                      </TableCell>
                      <TableCell>
                        {(
                          (Number(it.price) || 0) * (Number(it.qty) || 0)
                        ).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size='small'
                          color={it.remove ? 'default' : 'error'}
                          onClick={() => setItem(it.id, 'remove', !it.remove)}
                          title={it.remove ? 'Keep' : 'Remove'}>
                          <DeleteIcon fontSize='small' />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 1.5,
            }}>
            <Typography variant='body2' color='text.secondary'>
              New total (from items)
            </Typography>
            <Typography variant='h6' fontWeight={800} color='#2e7d32'>
              {Cur} {lineSum.toFixed(2)}
            </Typography>
          </Box>

          <Button
            fullWidth
            variant='contained'
            onClick={save}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color='inherit' /> : <SaveIcon />}
            sx={{ mt: 1.5, bgcolor: '#2e7d32' }}>
            {saving ? 'Saving...' : 'Save Order'}
          </Button>

          <LedgerEditor
            kind='order'
            ownerId={od.id}
            title='Order Ledger'
            color='#2e7d32'
          />
        </>
      )}
      {snackNode}
    </Paper>
  );
}

// ───────────────────────── Ledger editor (booking / order) ─────────────────
function LedgerEditor({ kind, ownerId, title, color }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showSnack, snackNode] = useSnack();
  const listPath =
    kind === 'booking'
      ? `booking-ledgers/${ownerId}`
      : `order-ledgers/${ownerId}`;
  const savePath = kind === 'booking' ? 'booking-ledger' : 'order-ledger';

  const load = async () => {
    try {
      const res = await fetch(`${config.API_BASE_URL}/correction/${listPath}`, {
        headers: authHeaders,
      });
      const data = res.ok ? await res.json() : [];
      setRows((Array.isArray(data) ? data : []).map((r) => ({ ...r })));
    } catch {
      setRows([]);
    }
  };
  useEffect(() => {
    if (ownerId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId]);

  const setField = (id, k, v) =>
    setRows((a) => a.map((r) => (r.id === id ? { ...r, [k]: v } : r)));

  const saveRow = async (row) => {
    setBusy(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/correction/${savePath}`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          Id: row.id,
          Amount: Number(row.amount) || 0,
          Mode: row.mode || '',
          Type: row.type || '',
          Remark: row.remark || '',
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) return showSnack(d?.error || 'Save failed', 'error');
      showSnack('Ledger row updated');
    } catch (e) {
      showSnack(e.message || 'Save failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const delRow = async (id) => {
    setBusy(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/correction/${savePath}/delete/${id}`,
        { method: 'POST', headers: authHeaders },
      );
      const d = await res.json().catch(() => ({}));
      if (!res.ok) return showSnack(d?.error || 'Delete failed', 'error');
      showSnack('Row deleted');
      load();
    } catch (e) {
      showSnack(e.message || 'Delete failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const addRow = async () => {
    setBusy(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/correction/booking-ledger/add`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            BookingId: ownerId,
            Amount: 0,
            Mode: 'Book',
            Type: 'DR',
            Remark: 'Manual entry',
            Companyid: company?.id || 0,
            Userid: userId,
          }),
        },
      );
      const d = await res.json().catch(() => ({}));
      if (!res.ok) return showSnack(d?.error || 'Add failed', 'error');
      showSnack('Row added');
      load();
    } catch (e) {
      showSnack(e.message || 'Add failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Divider sx={{ my: 2 }} />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}>
        <Typography variant='subtitle2' fontWeight={800} sx={{ color }}>
          {title} ({rows.length})
        </Typography>
        {kind === 'booking' && (
          <Button size='small' onClick={addRow} disabled={busy}>
            + Add Row
          </Button>
        )}
      </Box>
      {rows.length === 0 ? (
        <Typography variant='caption' color='text.secondary'>
          No ledger rows.
        </Typography>
      ) : (
        rows.map((r) => (
          <Box
            key={r.id}
            sx={{
              display: 'flex',
              gap: 0.5,
              mb: 1,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}>
            <TextField
              size='small'
              label='Mode'
              value={r.mode || ''}
              onChange={(e) => setField(r.id, 'mode', e.target.value)}
              sx={{ width: 120 }}
            />
            <TextField
              select
              size='small'
              label='Type'
              value={r.type || ''}
              onChange={(e) => setField(r.id, 'type', e.target.value)}
              sx={{ width: 80 }}>
              <MenuItem value='DR'>DR</MenuItem>
              <MenuItem value='CR'>CR</MenuItem>
            </TextField>
            <TextField
              size='small'
              type='number'
              label='Amount'
              value={r.amount ?? 0}
              onChange={(e) => setField(r.id, 'amount', e.target.value)}
              sx={{ width: 110 }}
            />
            <TextField
              size='small'
              label='Remark'
              value={r.remark || ''}
              onChange={(e) => setField(r.id, 'remark', e.target.value)}
              sx={{ flex: 1, minWidth: 120 }}
            />
            <Button
              size='small'
              variant='outlined'
              disabled={busy}
              onClick={() => saveRow(r)}>
              Save
            </Button>
            <IconButton
              size='small'
              color='error'
              disabled={busy}
              onClick={() => delRow(r.id)}>
              <DeleteIcon fontSize='small' />
            </IconButton>
          </Box>
        ))
      )}
      {snackNode}
    </>
  );
}

// ───────────────────────── Invoice editor ──────────────────────────────────
function InvoiceEditor({ bookingId }) {
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showSnack, snackNode] = useSnack();

  useEffect(() => {
    if (!bookingId) {
      setInv(null);
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${config.API_BASE_URL}/correction/invoice/${bookingId}`,
          { headers: authHeaders },
        );
        setInv(res.ok ? await res.json() : null);
      } catch {
        setInv(null);
      }
    })();
  }, [bookingId]);

  const setF = (k, v) => setInv((o) => ({ ...o, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/correction/invoice`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          Id: inv.id,
          Price: Number(inv.price) || 0,
          TotelPrice: Number(inv.totelPrice) || 0,
          Gst: Number(inv.gst) || 0,
          GstAmount: Number(inv.gstAmount) || 0,
          CGst: Number(inv.cGst) || 0,
          SGst: Number(inv.sGst) || 0,
          Igst: Number(inv.igst) || 0,
          Covercharge: Number(inv.covercharge) || 0,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) return showSnack(d?.error || 'Save failed', 'error');
      showSnack('Invoice updated');
    } catch (e) {
      showSnack(e.message || 'Save failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (!inv) return null;
  const field = (label, k) => (
    <Grid item xs={6}>
      <TextField
        fullWidth
        size='small'
        type='number'
        label={label}
        value={inv[k] ?? 0}
        onChange={(e) => setF(k, e.target.value)}
      />
    </Grid>
  );

  return (
    <>
      <Divider sx={{ my: 2 }} />
      <Typography variant='subtitle2' fontWeight={800} sx={{ mb: 1, color: '#6a1b9a' }}>
        Invoice #{inv.invoiceNo} — edit amounts &amp; GST
      </Typography>
      <Grid container spacing={1.5}>
        {field('Base Price', 'price')}
        {field('Total (with GST)', 'totelPrice')}
        {field('GST %', 'gst')}
        {field('GST Amount', 'gstAmount')}
        {field('CGST', 'cGst')}
        {field('SGST', 'sGst')}
        {field('IGST', 'igst')}
        {field('Cover Charge', 'covercharge')}
      </Grid>
      <Button
        fullWidth
        variant='contained'
        onClick={save}
        disabled={busy}
        startIcon={busy ? <CircularProgress size={16} color='inherit' /> : <SaveIcon />}
        sx={{ mt: 1.5, bgcolor: '#6a1b9a' }}>
        {busy ? 'Saving...' : 'Save Invoice'}
      </Button>
      {snackNode}
    </>
  );
}

export default function AmountCorrection() {
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f0f4f8', minHeight: '100vh' }}>
      <Typography variant='h5' fontWeight={800} sx={{ color: '#0a2540', mb: 0.5 }}>
        Booking &amp; Order Editor
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
        Load a booking or order by id and edit any field — guest details, dates,
        rooms, discount, amount, order items. Pending/ledger is adjusted to match.
      </Typography>
      <Grid container spacing={2} sx={{ maxWidth: 1200 }}>
        <Grid item xs={12} md={6}>
          <BookingEditor />
        </Grid>
        <Grid item xs={12} md={6}>
          <OrderEditor />
        </Grid>
      </Grid>
    </Box>
  );
}
